import { useState } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// Canvas hierarchy - shared by Review Mode and Live Review.
//
// The review (5.2) and live-review (7.4) payloads both return a flat list of
// `layer` entries, but the canvas is drawn as four bands - and four of those
// layers ("Customers & Promise", "Competitive Advantage", "Repeatable Model",
// "Economic Model") are sub-sections of a single Competitive Strategy band.
// Neither payload carries band, group or ordering information, so the
// hierarchy is declared here and mirrors the band/subsection tree already
// hard-coded in StrategyCanvasCanvasMode.js. Driving both review screens from
// one definition means a block can't appear under one heading on the canvas
// and a different one in the review list.
// ─────────────────────────────────────────────────────────────────────────────

// `color` matches the CanvasBand.scss modifiers (--teal / --dark / --core /
// --dna), which reuse the exact bar colours from StrategyCanvasCanvasMode.scss
// - the hex values stay in the stylesheets.
//
// ⚠️ ARRAY ORDER IS THE ACCORDION ORDER, and it is fixed here on instruction
// (2026-09-09):
//
//   Business DNA → Competitive Strategy → Core Strategy → Growth Strategy
//
// It used to run the other way round, Growth first and DNA last. Don't
// reorder these to match the canvas, which draws Growth at the top: the review
// list is read in the order the CONVERSATION goes, and the canvas is a picture
// read all at once. `buildBands` maps over this array, so moving an entry is
// the only thing needed to change the order.
export const REVIEW_BANDS = [
  {
    key: "dna",
    label: "Business DNA",
    color: "dna",
    sections: [
      {
        layer: "DNA",
        hideHeading: true,
        // The API returns mission/purpose/vision/values; the canvas prints
        // Mission/Vision/Purpose/Values.
        order: ["mission", "vision", "purpose", "values"],
      },
    ],
  },
  {
    key: "competitive",
    label: "Competitive Strategy",
    color: "dark",
    sections: [
      {
        layer: "Customers & Promise",
        hint: "(Where we play and how we win)",
        groups: [
          {
            label: "Customer Focus",
            blocks: [
              "target_segment",
              "general_needs",
              "niche_needs",
              "niche_positioning",
            ],
          },
          {
            label: "Remarkable Promise",
            blocks: ["unique_value_prop", "offering"],
          },
        ],
      },
      {
        layer: "Competitive Advantage",
        // The canvas draws the sources cell first, then Competitors; the API
        // returns them the other way round.
        order: ["competitive_advantage", "competitors"],
      },
      { layer: "Repeatable Model" },
      { layer: "Economic Model" },
    ],
  },
  {
    key: "core",
    label: "Core Strategy",
    color: "core",
    sections: [
      {
        layer: "Core",
        hideHeading: true,
        groups: [
          {
            label: "Core Business Strategy",
            blocks: ["full_potential", "primary_strategy"],
          },
          { label: null, blocks: ["adjacency_strategy"] },
        ],
      },
    ],
  },
  {
    key: "growth",
    label: "Growth Strategy",
    color: "teal",
    sections: [{ layer: "Growth", hideHeading: true }],
  },
];

// Used where the API's `label` is shorter than the label the canvas prints,
// so a block reads identically on both screens.
export const BLOCK_LABEL_OVERRIDES = {
  bhag: "Our BHAG (Big Hairy Audacious Goal)",
  competitive_advantage:
    "Sources & Description of Defendable Competitive Advantage",
  revenue_model: "Revenue Model, Pricing",
  unit_economics: "UE = CLTV / CAC",
  full_potential: "% of Full Potential Achieved",
  primary_strategy: "Primary Strategy to Grow Value",
  unique_value_prop: "Unique Value Proposition",
};

export const getBlockLabel = (block) =>
  BLOCK_LABEL_OVERRIDES[block.block_key] || block.label;

// Splits one layer's blocks into the groups the canvas draws. Anything the
// config doesn't mention still comes through - in a trailing unlabelled group
// - so a newly-added API block shows up rather than silently disappearing.
const groupBlocks = (section, blocks) => {
  const byKey = new Map(blocks.map((block) => [block.block_key, block]));
  const take = (keys) => keys.map((key) => byKey.get(key)).filter(Boolean);

  if (section.groups) {
    const claimed = new Set(section.groups.flatMap((group) => group.blocks));
    const groups = section.groups
      .map((group) => ({
        label: group.label || null,
        blocks: take(group.blocks),
      }))
      .filter((group) => group.blocks.length > 0);
    const rest = blocks.filter((block) => !claimed.has(block.block_key));
    return rest.length ? [...groups, { label: null, blocks: rest }] : groups;
  }

  if (section.order) {
    const ordered = new Set(section.order);
    const rest = blocks.filter((block) => !ordered.has(block.block_key));
    return [{ label: null, blocks: [...take(section.order), ...rest] }];
  }

  return [{ label: null, blocks }];
};

// Turns the payload's flat `layers` into the band > section > group > block
// tree both review screens render.
//
// getStatus differs per screen: Review Mode's blocks carry `review_status`
// ("accepted" | "to_discuss" | null) while Live Review's carry `status`, so
// the caller supplies the accessor and each band reports its own progress.
export const buildBands = (
  layers = [],
  getStatus = (block) => block.review_status,
) => {
  const safeLayers = layers || [];
  const byLayer = new Map(safeLayers.map((layer) => [layer.layer, layer]));
  const seen = new Set();

  const toBand = ({ key, label, color, sections }) => {
    const built = sections
      .map((section) => {
        const layer = byLayer.get(section.layer);
        if (!layer) return null;
        seen.add(section.layer);
        return {
          key: section.layer,
          title: section.layer,
          hint: section.hint || null,
          hideHeading: !!section.hideHeading,
          layer,
          groups: groupBlocks(section, layer.blocks || []),
        };
      })
      .filter(Boolean);
    if (!built.length) return null;

    const blocks = built.flatMap((section) =>
      section.groups.flatMap((group) => group.blocks),
    );
    return {
      key,
      label,
      color,
      sections: built,
      totalCount: blocks.length,
      reviewedCount: blocks.filter((block) => !!getStatus(block)).length,
    };
  };

  const bands = REVIEW_BANDS.map((band) => toBand(band)).filter(Boolean);

  // Any layer the config doesn't know about becomes its own band, so an API
  // change surfaces in the UI instead of dropping out of it.
  safeLayers.forEach((layer) => {
    if (seen.has(layer.layer)) return;
    const band = toBand({
      key: layer.layer,
      label: layer.layer,
      color: "core",
      sections: [{ layer: layer.layer, hideHeading: true }],
    });
    if (band) bands.push(band);
  });

  return bands;
};

// The band both review screens open on. Business DNA is last on the canvas but
// first in the conversation - mission/vision/purpose/values are what the rest
// of the strategy has to be consistent with, so the coach reads them first.
//
// Since 2026-09-09 it is also FIRST IN THE LIST, so this now opens the top
// band rather than jumping the coach down to the bottom one. Worth knowing if
// the order ever changes again: this key and `REVIEW_BANDS[0]` agreeing is a
// coincidence of the current order, not something enforced.
export const INITIAL_OPEN_BAND_KEY = "dna";

// First block of a band, in canvas reading order. Scans rather than indexing
// straight to [0][0][0]: buildBands drops empty sections and groups, but a
// band whose blocks all failed to match could still leave an empty shell
// behind, and indexing blind would return undefined and silently open nothing.
export const getFirstBlockKey = (band) => {
  for (const section of band?.sections || []) {
    for (const group of section.groups || []) {
      const block = (group.blocks || [])[0];
      if (block?.block_key) return block.block_key;
    }
  }
  return null;
};

/**
 * Owns the two pieces of "where the coach lands" state: which bands are folded
 * shut, and which single block accordion is open.
 *
 * Both start focused rather than fully open. Every band expanded is four
 * bands' worth of rows to scroll past before the coach has decided anything;
 * everything shut means their first action is a throwaway click just to see
 * what a row contains. So: Business DNA open, its first block expanded, the
 * rest folded away until wanted.
 *
 * Primes once per mount, not on every `bands` change. Without that guard the
 * bands would re-collapse and the accordion spring back open on the next poll
 * or socket update, undoing whatever the coach had just opened.
 *
 * @param {Array} bands Output of buildBands - empty until the payload arrives.
 */
export const useInitialCanvasFocus = (bands) => {
  // Empty set = nothing collapsed. Replaced the moment real bands arrive.
  const [collapsedBands, setCollapsedBands] = useState(() => new Set());
  const [expandedBlockKey, setExpandedBlockKey] = useState(null);
  const [primed, setPrimed] = useState(false);

  // Set while rendering rather than in an effect. An effect runs after the
  // commit, so the browser would paint one frame with every band open before
  // they collapsed - a visible flicker on the one thing the coach is looking
  // at. Adjusting state during render is React's supported escape hatch: it
  // re-runs the component immediately, before any paint.
  //
  // `primed` has to be state, not a ref. StrictMode invokes render twice to
  // surface impure renders, and a ref flipped during the first invoke is still
  // flipped on the second - so the second pass skipped the updates entirely
  // and every band rendered open. State is rolled back between the two
  // invokes, so both passes see `primed === false`, do the identical work, and
  // land on the same result.
  if (!primed && bands.length) {
    setPrimed(true);

    // Fall back to the first band if Business DNA isn't in the payload, so an
    // unexpected layer set still lands somewhere sensible instead of leaving
    // the whole canvas shut.
    const focusBand =
      bands.find((band) => band.key === INITIAL_OPEN_BAND_KEY) || bands[0];

    setCollapsedBands(
      new Set(
        bands
          .filter((band) => band.key !== focusBand.key)
          .map((band) => band.key),
      ),
    );
    setExpandedBlockKey(getFirstBlockKey(focusBand));
  }

  return {
    collapsedBands,
    setCollapsedBands,
    expandedBlockKey,
    setExpandedBlockKey,
  };
};
